import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import type {
  Situation,
  SituationCategory,
  SupportedLanguage,
  MindstringPrototype,
  TargetOutput,
  OriginOutput,
} from "@/core/fastapiClient/sdk";

export interface UserChoiceState {
  //////* Language
  selectedLanguage: SupportedLanguage | null;
  setSelectedLanguage: (language: SupportedLanguage | null) => void;
  resetSelectedLanguage: () => void;

  //////* Situation categories
  selectedSituationCategories: SituationCategory[] | null;
  setSelectedSituationCategories: (
    categories: SituationCategory[] | null
  ) => void;
  resetSelectedSituationCategories: () => void;

  //////* Mindstring Prototype
  selectedMindstringPrototype: MindstringPrototype | null;
  setSelectedMindstringPrototype: (
    prototype: MindstringPrototype | null
  ) => void;
  resetSelectedMindstringPrototype: () => void;

  //////* Situation
  selectedSituation: Situation | null;
  setSelectedSituation: (situation: Situation | null) => void;
  resetSelectedSituation: () => void;
  // new situation
  newSituation: Situation | null;
  setNewSituation: (situation: Situation | null) => void;
  resetNewSituation: () => void;
  // manage dialog
  situationDialogMode: "add" | "edit" | null;
  setSituationDialogMode: (mode: "add" | "edit" | null) => void;
  resetSituationDialogMode: () => void;
  // situation to be edited
  situationToEdit: Situation | null;
  setSituationToEdit: (situation: Situation | null) => void;
  resetSituationToEdit: () => void;

  //////* Target
  selectedTarget: TargetOutput | null;
  setSelectedTarget: (target: TargetOutput | null) => void;
  resetSelectedTarget: () => void;
  // new target
  newTarget: TargetOutput | null;
  setNewTarget: (target: TargetOutput | null) => void;
  resetNewTarget: () => void;
  // manage dialog
  targetDialogMode: "add" | "edit" | null;
  setTargetDialogMode: (mode: "add" | "edit" | null) => void;
  resetTargetDialogMode: () => void;
  // target to be edited
  targetToEdit: TargetOutput | null;
  setTargetToEdit: (target: TargetOutput | null) => void;
  resetTargetToEdit: () => void;
  // preview target
  isPreviewOpen: boolean;
  setPreviewOpen: (isOpen: boolean) => void;

  //////* Origin upload
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  resetSelectedFile: () => void;
  // file upload clear trigger
  fileKeyTrigger: number;
  setFileKeyTrigger: (key: number) => void;
  // new Origin
  newOrigin: OriginOutput | null;
  setNewOrigin: (origin: OriginOutput | null) => void;
  resetNewOrigin: () => void;

  //////* Insert thoughts
  isInsertThoughtsDialogOpen: boolean;
  whereInsertThoughtsDialog: {
    situation: Situation | null;
    category: SituationCategory | null;
  };
  openInsertThoughtsDialog: (
    situation: Situation,
    category: SituationCategory
  ) => void;
  closeInsertThoughtsDialog: () => void;

  //////* Extract thoughts
  isOpenEditorDialog: boolean;
  setIsOpenEditorDialog: (isOpen: boolean) => void;
  editorContent: string;
  setEditorContent: (content: string) => void;

  //////* Questionnaire
  isOpenQuestionnaireDialog: boolean;
  setIsOpenQuestionnaireDialog: (isOpen: boolean) => void;
}

export const createUserChoicesSlice: StateCreator<
  GlobalState,
  [],
  [],
  UserChoiceState
> = (set) => ({
  //////* Languages
  selectedLanguage: null,
  setSelectedLanguage: (language) => set({ selectedLanguage: language }),
  resetSelectedLanguage: () => set({ selectedLanguage: null }),

  //////* Situation categories
  selectedSituationCategories: null,
  setSelectedSituationCategories: (categories) =>
    set({ selectedSituationCategories: categories }),
  resetSelectedSituationCategories: () =>
    set({ selectedSituationCategories: null }),

  //////* Mindstring Prototype
  selectedMindstringPrototype: null,
  setSelectedMindstringPrototype: (prototype) =>
    set({ selectedMindstringPrototype: prototype }),
  resetSelectedMindstringPrototype: () =>
    set({ selectedMindstringPrototype: null }),

  //////* Situation
  selectedSituation: null,
  setSelectedSituation: (situation) => set({ selectedSituation: situation }),
  resetSelectedSituation: () => set({ selectedSituation: null }),
  // new situation
  newSituation: null,
  setNewSituation: (situation) => set({ newSituation: situation }),
  resetNewSituation: () => set({ newSituation: null }),
  // manage dialog
  situationDialogMode: null,
  setSituationDialogMode: (mode) => set({ situationDialogMode: mode }),
  resetSituationDialogMode: () => set({ situationDialogMode: null }),
  // situation to be edited
  situationToEdit: null,
  setSituationToEdit: (situation) => set({ situationToEdit: situation }),
  resetSituationToEdit: () => set({ situationToEdit: null }),

  //////* Target
  selectedTarget: null,
  setSelectedTarget: (target) => set({ selectedTarget: target }),
  resetSelectedTarget: () => set({ selectedTarget: null }),
  // new target
  newTarget: null,
  setNewTarget: (target) => set({ newTarget: target }),
  resetNewTarget: () => set({ newTarget: null }),
  // manage dialog
  targetDialogMode: null,
  setTargetDialogMode: (mode) => set({ targetDialogMode: mode }),
  resetTargetDialogMode: () => set({ targetDialogMode: null }),
  // target to be edited
  targetToEdit: null,
  setTargetToEdit: (target) => set({ targetToEdit: target }),
  resetTargetToEdit: () => set({ targetToEdit: null }),
  // preview target
  isPreviewOpen: false,
  setPreviewOpen: (isOpen) => set({ isPreviewOpen: isOpen }),

  //////* Origin
  selectedFile: null,
  setSelectedFile: (file) => set({ selectedFile: file }),
  resetSelectedFile: () => set({ selectedFile: null }),
  // file upload clear trigger
  fileKeyTrigger: 0,
  setFileKeyTrigger: (key) => set({ fileKeyTrigger: key }),
  // new Origin
  newOrigin: null,
  setNewOrigin: (origin) => set({ newOrigin: origin }),
  resetNewOrigin: () => set({ newOrigin: null }),

  //////* Insert thoughts
  isInsertThoughtsDialogOpen: false,
  whereInsertThoughtsDialog: {
    situation: null,
    category: null,
  },
  openInsertThoughtsDialog: (situation, category) =>
    set({
      isInsertThoughtsDialogOpen: true,
      whereInsertThoughtsDialog: { situation, category },
    }),
  closeInsertThoughtsDialog: () =>
    set({
      isInsertThoughtsDialogOpen: false,
      whereInsertThoughtsDialog: { situation: null, category: null },
    }),

  //////* Extract thoughts
  isOpenEditorDialog: false,
  setIsOpenEditorDialog: (isOpen) => set({ isOpenEditorDialog: isOpen }),
  editorContent: "",
  setEditorContent: (content) => set({ editorContent: content }),

  //////* Questionnaire
  isOpenQuestionnaireDialog: false,
  setIsOpenQuestionnaireDialog: (isOpen) =>
    set({ isOpenQuestionnaireDialog: isOpen }),
});
